import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import {
  Box,
  Typography,
  FormHelperText,
  Tooltip,
  Paper,
  Toolbar,
  FormControl,
  Divider,
  Select,
  MenuItem,
} from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import Measure from 'react-measure'
import { debounce } from 'lodash'

import { colors } from 'common/colors'
import ErrorMessage from '../ErrorMessage'
import { Pill } from 'components/Pill'
import LoadingBubbles from 'components/LoadingBubbles'

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '100%',
    minHeight: 545,
  },
  toolbar: {
    padding: theme.spacing(2),
  },
  title: {
    flex: '1 1 100%',
  },
  formControl: {
    minWidth: 200,
  },
  graphContainer: {
    height: 452,
    padding: theme.spacing(2),
    paddingTop: theme.spacing(6),
  },
  infoIcon: {
    color: colors.primary.main,
    marginLeft: theme.spacing(1),
  },
  disabled: {
    color: theme.palette.text.disabled,
  },
  loadingBubbles: {
    width: '100%',
    height: '100%',
  },
}))

const formatNumber = (number) => (number || 0).toLocaleString()
const formatPercent = (numerator, denominator) => {
  if (!numerator || !denominator) return '0%'

  return ((numerator / denominator) * 100).toFixed() + '%'
}

const projectYear = (current, previous) => {
  if (!current || !previous) return null

  const applications = Math.floor((current.interested * previous.applications) / previous.interested || 0)
  const accepted = Math.floor((applications * previous.accepted) / previous.applications || 0)
  const enrolled = Math.floor((accepted * previous.enrolled) / previous.accepted || 0)

  return {
    graduation_year: current.graduation_year,
    interested: current.interested,
    applications,
    accepted,
    enrolled,
    isMissing: current.isMissing,
  }
}

const ConversionByYearCard = ({ data, seniorGradYear, loading, error }) => {
  const hasNoData = !data || !data.length
  const sortedData = useMemo(() => {
    if (loading || error || hasNoData) return []

    const sortedData = [...data].sort((a, b) => b.graduation_year - a.graduation_year)
    if (!sortedData.length || sortedData[0].graduation_year !== seniorGradYear) {
      sortedData.splice(0, 0, {
        graduation_year: seniorGradYear,
        interested: 0,
        applications: 0,
        accepted: 0,
        enrolled: 0,
        isMissing: true,
      })
    }

    return sortedData
  }, [data, error, hasNoData, loading, seniorGradYear])

  const currentYear = useMemo(
    () => (sortedData.length && sortedData[0].graduation_year >= seniorGradYear ? sortedData[0] : null),
    [sortedData, seniorGradYear],
  )

  const previousYear = currentYear && sortedData.find((d) => d.graduation_year < currentYear.graduation_year)

  const projectedYear = projectYear(currentYear, previousYear)

  const [selectedClassYearOption, setSelectedClassYearOption] = useState('current')
  const [selectedClassYear, setSelectedClassYear] = useState({ graduation_year: '' })
  const [height, setHeight] = useState(undefined)
  const classes = useStyles()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('xs'))

  useEffect(() => {
    if (currentYear) setSelectedClassYear(currentYear)
  }, [currentYear])

  const options = (() => {
    const options = []

    if (projectedYear && currentYear) {
      options.push(
        { label: `Class of ${projectedYear.graduation_year} (Projected)`, value: 'projected' },
        { label: `Class of ${currentYear.graduation_year} (In Progress)`, value: 'current' },
      )
    } else if (currentYear) {
      options.push({ label: `Class of ${currentYear.graduation_year} (In Progress)`, value: 'current' })
    }

    sortedData
      .slice(1)
      .forEach((year) =>
        options.push({ label: `Class of ${year.graduation_year.toString()}`, value: year.graduation_year.toString() }),
      )
    return options
  })()

  const debounceResize = useMemo(
    () =>
      debounce((newWidth, newHeight) => {
        if (isSmall) {
          setHeight(newHeight - 94 - 54)
        }
      }),
    [isSmall, setHeight],
  )

  const handleChange = (event) => {
    const value = event.target.value

    const data = (() => {
      switch (value) {
        case 'projected':
          return projectedYear
        case 'current':
          return currentYear
        default:
          return sortedData.find((d) => d.graduation_year === Number.parseInt(value))
      }
    })()

    setSelectedClassYear(data)
    setSelectedClassYearOption(value)
  }

  const renderError = () => <ErrorMessage title="Sorry, we don’t currently have any data for this selection." />

  const renderNoData = () => (
    <ErrorMessage
      title="It looks like you don’t have any Interested Student data."
      subtitle="Try creating some content or contacting an administrator for help getting started."
    />
  )

  const renderSvg = () => (
    <svg
      height="100%"
      width="100%"
      viewBox={`0 0 370 440`}
      fill="none"
    >
      {/* Interested segment */}
      <path
        d="M131.256 0H255.165C260.192 0 263.58 5.14086 261.598 9.76028L223.376 98.8368C222.272 101.409 219.742 103.077 216.943 103.077H131.256V0Z"
        fill="#9FE0FF"
      />
      <path
        d="M137.878 0H13.643C8.61055 0 5.22226 5.15162 7.2155 9.77253L45.5208 98.5753C46.6276 101.141 49.1541 102.803 51.9484 102.803H137.878V0Z"
        fill="#9FE0FF"
      />

      {/* Interested label */}
      <foreignObject
        x="276"
        y="0"
        width="90"
        height="40"
        requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
      >
        <Typography
          align="right"
          variant="body2"
          color="textSecondary"
        >
          Interested
        </Typography>
      </foreignObject>

      {/* Interested count */}
      {loading ? (
        <foreignObject
          x="336"
          y="20"
          width="30"
          height="30"
          requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
        >
          <LoadingBubbles
            className={classes.loadingBubbles}
            altText="Loading data"
          />
        </foreignObject>
      ) : (
        <foreignObject
          x="276"
          y="20"
          width="90"
          height="40"
          requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
        >
          <Typography
            className={selectedClassYear.isMissing && classes.disabled}
            align="right"
            variant="h5"
            color="textPrimary"
          >
            {formatNumber(selectedClassYear.interested)}
          </Typography>
        </foreignObject>
      )}

      {/* Interested -> Applied % */}
      {!selectedClassYear.isMissing && !loading && (
        <foreignObject
          x="0"
          y="98"
          width="40"
          height="28"
          requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
        >
          <Pill
            variant="info"
            caption={formatPercent(selectedClassYear.applications, selectedClassYear.interested)}
          />
        </foreignObject>
      )}

      {/* Interested -> Application line */}
      <path
        d="M57.3046 110.822C57.0284 110.823 56.8049 111.047 56.8052 111.323C56.8055 111.599 57.0296 111.823 57.3058 111.822L57.3046 110.822ZM57.3058 111.822L373.198 111.514L373.197 110.514L57.3046 110.822L57.3058 111.822Z"
        fill="#AAAAAA"
        fillOpacity="0.5"
      />

      {/* Applied segment */}
      <path
        d="M131.256 117.802H206.712C211.453 117.802 214.822 122.415 213.38 126.931L185.558 214.045C184.631 216.946 181.935 218.915 178.89 218.915H131.256V117.802Z"
        fill="#6ED0FF"
      />
      <path
        d="M137.793 117.508H62.0365C57.2968 117.508 53.9273 122.119 55.3675 126.635L83.3086 214.239C84.2344 217.141 86.9311 219.111 89.9776 219.111H137.793V117.508Z"
        fill="#6ED0FF"
      />

      {/* Applied label */}
      <foreignObject
        x="276"
        y="120.15"
        width="90"
        height="40"
        requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
      >
        <Typography
          align="right"
          variant="body2"
          color="textSecondary"
        >
          Applied
        </Typography>
      </foreignObject>

      {/* Application count */}
      {loading ? (
        <foreignObject
          x="336"
          y="140.15"
          width="30"
          height="30"
          requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
        >
          <LoadingBubbles
            className={classes.loadingBubbles}
            altText="Loading data"
          />
        </foreignObject>
      ) : (
        <foreignObject
          x="276"
          y="140.15"
          width="90"
          height="40"
          requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
        >
          <Typography
            className={selectedClassYear.isMissing && classes.disabled}
            align="right"
            variant="h5"
            color="textPrimary"
          >
            {formatNumber(selectedClassYear.applications)}
          </Typography>
        </foreignObject>
      )}

      {/* Applied -> Accepted % */}
      {!selectedClassYear.isMissing && !loading && (
        <foreignObject
          x="32"
          y="214"
          width="40"
          height="28"
          requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
        >
          <Pill
            variant="info"
            caption={formatPercent(selectedClassYear.accepted, selectedClassYear.applications)}
          />
        </foreignObject>
      )}

      {/* Applied -> Accepted line */}
      <path
        d="M86.3986 227.446C86.1225 227.445 85.8981 227.669 85.8975 227.945C85.8968 228.221 86.1201 228.445 86.3963 228.446L86.3986 227.446ZM86.3963 228.446L373.196 229.008L373.199 228.008L86.3986 227.446L86.3963 228.446Z"
        fill="#AAAAAA"
        fillOpacity="0.5"
      />

      {/* Accepted segment */}
      <path
        d="M131.257 233.64H174.011C178.521 233.64 181.854 237.844 180.826 242.236L170.388 286.827L159.937 330.369C159.181 333.516 156.367 335.735 153.13 335.735H131.257V233.64Z"
        fill="#32B4F2"
      />
      <path
        d="M137.878 233.643H94.9276C90.4122 233.643 87.0781 237.854 88.1145 242.249L98.5867 286.658L109.079 330.052C109.839 333.194 112.651 335.407 115.883 335.407H137.878V233.643Z"
        fill="#32B4F2"
      />

      {/* Accepted label */}
      <foreignObject
        x="276"
        y="235.154"
        width="90"
        height="40"
        requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
      >
        <Typography
          align="right"
          variant="body2"
          color="textSecondary"
        >
          Accepted
        </Typography>
      </foreignObject>

      {/* Accepted count */}
      {loading ? (
        <foreignObject
          x="336"
          y="255.154"
          width="30"
          height="30"
          requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
        >
          <LoadingBubbles
            className={classes.loadingBubbles}
            altText="Loading data"
          />
        </foreignObject>
      ) : (
        <foreignObject
          x="276"
          y="255.154"
          width="90"
          height="40"
          requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
        >
          <Typography
            className={selectedClassYear.isMissing && classes.disabled}
            align="right"
            variant="h5"
            color="textPrimary"
          >
            {formatNumber(selectedClassYear.accepted)}
          </Typography>
        </foreignObject>
      )}

      {/* Accepted -> Enrolled % */}
      {!selectedClassYear.isMissing && !loading && (
        <foreignObject
          x="63"
          y="328"
          width="40"
          height="28"
          requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
        >
          <Pill
            variant="info"
            caption={formatPercent(selectedClassYear.enrolled, selectedClassYear.accepted)}
          />
        </foreignObject>
      )}

      {/* Accepted -> Enrolled line */}
      <path
        d="M111.613 341.419C111.337 341.418 111.112 341.641 111.111 341.917C111.11 342.193 111.333 342.418 111.609 342.419L111.613 341.419ZM111.609 342.419L375.377 343.302L375.381 342.302L111.613 341.419L111.609 342.419Z"
        fill="#AAAAAA"
        fillOpacity="0.5"
      />

      {/* Enrolled segment */}
      <path
        d="M131.871 348.225H148.363C152.77 348.225 156.081 352.249 155.232 356.574L150.836 378.962L146.278 401.607C145.62 404.874 142.749 407.225 139.415 407.225H131.871V348.225Z"
        fill="#13A2E6"
      />
      <path
        d="M134.871 348.225H118.379C113.971 348.225 110.661 352.249 111.51 356.574L115.905 378.962L120.464 401.607C121.122 404.874 123.993 407.225 127.326 407.225H134.871V348.225Z"
        fill="#13A2E6"
      />

      {/* Enrolled label */}
      <foreignObject
        x="276"
        y="348.225154"
        width="90"
        height="40"
        requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
      >
        <Typography
          align="right"
          variant="body2"
          color="textSecondary"
        >
          Enrolled
        </Typography>
      </foreignObject>

      {/* Enrolled count */}
      {loading ? (
        <foreignObject
          x="336"
          y="368.225154"
          width="30"
          height="30"
          requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
        >
          <LoadingBubbles
            className={classes.loadingBubbles}
            altText="Loading data"
          />
        </foreignObject>
      ) : (
        <foreignObject
          x="276"
          y="368.225154"
          width="90"
          height="40"
          requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
        >
          <Typography
            className={selectedClassYear.isMissing && classes.disabled}
            align="right"
            variant="h5"
            color="textPrimary"
          >
            {formatNumber(selectedClassYear.enrolled)}
          </Typography>
        </foreignObject>
      )}

      {/* projected help text */}
      {selectedClassYearOption === 'projected' && (
        <foreignObject
          x="192"
          y="418"
          width="180"
          height="40"
          requiredFeatures="http://www.w3.org/TR/SVG11/feature#Extensibility"
        >
          <FormHelperText>
            How is this data calculated?
            <Tooltip
              arrow
              title="These projections are based on the current Interested Student count and last year’s conversion rates and can change as the academic year progresses."
            >
              {/*
                                NOTE: The <Tooltip /> component requires its children to accept a ref
                                and the <FontAwesomeIcon /> component does not. The <span /> component
                                is here purely to address this issue.
                            */}
              <span className={classes.infoIcon}>
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className={'fa-lg'}
                />
              </span>
            </Tooltip>
          </FormHelperText>
        </foreignObject>
      )}
    </svg>
  )

  const renderContent = () => {
    if (error) return renderError()

    if (hasNoData && !loading) return renderNoData()

    return renderSvg()
  }

  return (
    <Measure
      onResize={(contentRect) => {
        if (contentRect && contentRect.entry) {
          debounceResize(contentRect.entry.width, contentRect.entry.height)
        }
      }}
    >
      {({ measureRef }) => (
        <Paper
          ref={measureRef}
          className={classes.paper}
          elevation={0}
        >
          <Toolbar className={classes.toolbar}>
            <Typography
              className={classes.title}
              variant="body1"
              color="textPrimary"
            >
              Conversion by Class Year
            </Typography>
            <FormControl
              className={classes.formControl}
              variant="outlined"
              size="small"
            >
              {options && options.length > 0 && (
                <Select
                  labelId="engagement-by-month-select-label"
                  id="engagement-by-month-select"
                  defaultValue=""
                  value={selectedClassYearOption}
                  onChange={handleChange}
                  readOnly={loading}
                  disabled={loading || error || hasNoData}
                  aria-label="class year options"
                >
                  {options.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FormControl>
          </Toolbar>
          <Divider light />
          <Box
            className={classes.graphContainer}
            height={height}
          >
            {renderContent()}
          </Box>
        </Paper>
      )}
    </Measure>
  )
}

ConversionByYearCard.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      graduation_year: PropTypes.number.isRequired,
      interested: PropTypes.number.isRequired,
      applications: PropTypes.number.isRequired,
      accepted: PropTypes.number.isRequired,
      enrolled: PropTypes.number.isRequired,
    }),
  ).isRequired,
  seniorGradYear: PropTypes.number.isRequired,
  error: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
}

export default ConversionByYearCard
